import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { supabase } from '../services/supabaseClient';
import { useAuth } from './AuthContext';

// ─── Browser Web Push integration ─────────────────────────────────────
//
// Responsibilities:
//   1. Register the service worker (/sw.js) once on app load.
//   2. Expose the current Notification permission state + capabilities to
//      the rest of the React tree via useNotifications().
//   3. Provide enable/disable methods that handle the full subscribe /
//      unsubscribe dance and persist to the push_subscriptions table.
//
// Why a context: lots of pages want to know "is this user push-enabled?"
// (Settings toggle, banner prompts, debug screens). One source of truth
// avoids race conditions where two parts of the UI disagree on state.

const PushContext = createContext(null);

function isSupported() {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager'    in window
    && 'Notification'   in window;
}

// VAPID public key from the build env. Set REACT_APP_VAPID_PUBLIC_KEY in
// your .env so the client can subscribe with the same key the server uses
// to sign push messages.
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

// Standard helper: url-safe base64 → Uint8Array, expected by subscribe()'s
// applicationServerKey option.
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function bufToB64(buf) {
  if (!buf) return '';
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  // url-safe base64
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function PushNotificationsProvider({ children }) {
  const { user } = useAuth();
  const [supported, setSupported]     = useState(false);
  const [permission, setPermission]   = useState(typeof Notification !== 'undefined' ? Notification.permission : 'default');
  const [subscribed, setSubscribed]   = useState(false);
  const [registration, setRegistration] = useState(null);
  const [error, setError]             = useState('');

  // Register the service worker on mount — production only. In dev,
  // CRA's hot-reload constantly re-installs the SW, which (combined with
  // skipWaiting and the auto-reload listener in ConnectionStatus) causes
  // an infinite refresh loop. Push notifications and the app-shell cache
  // are production features anyway.
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      // One-shot cleanup: if a previous (broken) dev SW is still controlling
      // this origin, unregister it so the loop stops. Cheap when none exists.
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations()
          .then((regs) => regs.forEach((r) => r.unregister()))
          .catch(() => { /* ignore */ });
      }
      setSupported(false);
      return;
    }
    if (!isSupported()) { setSupported(false); return; }
    setSupported(true);
    navigator.serviceWorker.register('/sw.js')
      .then((reg) => {
        setRegistration(reg);
        // Forward notification clicks so the app can navigate via its
        // hash router (the sw posts {type:'docketflow.push.open', url}).
        navigator.serviceWorker.addEventListener('message', (event) => {
          if (event.data?.type === 'docketflow.push.open' && event.data.url) {
            window.location.hash = event.data.url.startsWith('#') ? event.data.url : `#${event.data.url}`;
          }
        });
      })
      .catch((err) => {
        // Service worker registration is best-effort. A failure here just
        // means push won't work in this browser/session.
        // eslint-disable-next-line no-console
        console.warn('[push] SW register failed:', err.message);
      });
  }, []);

  // Whenever the service worker is ready and the user is signed in, check
  // whether they already have a live subscription. Keeps the "Enabled" pill
  // in Settings accurate after a refresh.
  useEffect(() => {
    if (!registration || !user) { setSubscribed(false); return; }
    let cancelled = false;
    registration.pushManager.getSubscription().then((sub) => {
      if (cancelled) return;
      setSubscribed(!!sub);
    }).catch(() => { /* ignore — treat as not subscribed */ });
    return () => { cancelled = true; };
  }, [registration, user]);

  const enable = useCallback(async () => {
    setError('');
    if (!isSupported() || !registration) {
      setError('Push notifications are not supported in this browser.');
      return false;
    }
    if (!VAPID_PUBLIC_KEY) {
      setError('Push notifications are not configured on the server (missing VAPID public key).');
      return false;
    }

    // Ask for OS permission. This MUST be called from a user gesture or
    // some browsers will silently deny.
    const perm = await Notification.requestPermission();
    setPermission(perm);
    if (perm !== 'granted') {
      setError('Notification permission was denied. You can re-enable it in your browser settings.');
      return false;
    }

    // Subscribe (or reuse an existing subscription).
    let sub;
    try {
      sub = await registration.pushManager.getSubscription();
      if (!sub) {
        sub = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
        });
      }
    } catch (err) {
      setError(err.message || 'Failed to subscribe.');
      return false;
    }

    // Persist to push_subscriptions via the RPC. Same endpoint upserts cleanly.
    const json = sub.toJSON();
    const { error: rpcErr } = await supabase.rpc('register_push_subscription', {
      p_endpoint:     json.endpoint,
      p_p256dh:       json.keys?.p256dh || bufToB64(sub.getKey?.('p256dh')),
      p_auth:         json.keys?.auth   || bufToB64(sub.getKey?.('auth')),
      p_device_label: typeof navigator !== 'undefined' ? navigator.userAgent?.slice(0, 80) : null,
    });
    if (rpcErr) {
      setError(rpcErr.message || 'Could not register subscription.');
      return false;
    }
    setSubscribed(true);
    return true;
  }, [registration]);

  const disable = useCallback(async () => {
    setError('');
    if (!registration) return false;
    try {
      const sub = await registration.pushManager.getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        await supabase.rpc('unregister_push_subscription', { p_endpoint: endpoint });
      }
      setSubscribed(false);
      return true;
    } catch (err) {
      setError(err.message || 'Could not unsubscribe.');
      return false;
    }
  }, [registration]);

  // Convenience: send a test notification to the current device only. Hits
  // the send-web-push edge function with the caller as the target.
  const sendTest = useCallback(async () => {
    setError('');
    const { data, error: err } = await supabase.functions.invoke('send-web-push', {
      body: {
        target: 'self',
        title:  'DocketFlow test',
        body:   'Push notifications are working on this device.',
      },
    });
    if (err?.message) setError(err.message);
    else if (data?.error) setError(data.error);
  }, []);

  return (
    <PushContext.Provider value={{
      supported, permission, subscribed, error, enable, disable, sendTest,
      configured: !!VAPID_PUBLIC_KEY,
    }}>
      {children}
    </PushContext.Provider>
  );
}

export const useNotifications = () => useContext(PushContext) || {
  supported: false, permission: 'default', subscribed: false, error: '',
  enable: async () => false, disable: async () => false, sendTest: async () => {},
  configured: false,
};
